import { alpha, Box, ButtonBase, Stack, Typography, useTheme } from "@mui/material";
import AddRounded from "@mui/icons-material/AddRounded";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import BlockTimeModal from "./BlockTimeModal";
import AddEventModal from "./AddEventModal";
import { FONTS, CARD_STYLES } from "../../../constants/styles";

dayjs.extend(utc);

export interface FreeBookingSlot {
    start: dayjs.Dayjs;
    end: dayjs.Dayjs;
    working?: boolean;
}

const FreeSlotItem = (props: {
    uuid: string;
    brand_id: string;
    slot: FreeBookingSlot;
    isNonWorkingDay: boolean;
    onBlockSlot: (start: dayjs.Dayjs, end: dayjs.Dayjs, note?: string) => void;
}) => {
    const theme = useTheme();
    const navigate = useNavigate();
    const hex = theme.palette.primary.main;

    const { slot, onBlockSlot } = props;

    const [chooserOpen, setChooserOpen] = useState(false);
    const [blockModalOpen, setBlockModalOpen] = useState(false);

    const handleOpenChooser = () => setChooserOpen(true);
    const handleCloseChooser = () => setChooserOpen(false);

    const handleServiceAppointment = () => {
        sessionStorage.setItem(
            "slot_start",
            slot.start.format("YYYY-MM-DDTHH:mm:ssZ")
        );
        sessionStorage.setItem(
            "slot_end",
            slot.end.format("YYYY-MM-DDTHH:mm:ssZ")
        );
        setChooserOpen(false);
        navigate("/calendar/add/user");
    };

    const handlePrivateMatter = () => {
        setChooserOpen(false);
        setBlockModalOpen(true);
    };

    const handleConfirmBlock = async (
        start: dayjs.Dayjs,
        end: dayjs.Dayjs,
        note?: string
    ) => {
        await onBlockSlot(start, end, note);
        setBlockModalOpen(false);
    };

    const time = slot.end.diff(slot.start, "minute");
    const hours = Math.floor(time / 60);
    const mins = Math.floor(time % 60);

    const isWorking = !!slot.working;

    const bgColor = isWorking ? alpha(hex, 0.04) : "rgba(0,0,0,0.03)";
    const borderStyle = isWorking
        ? `1.5px dashed ${alpha(hex, 0.2)}`
        : "none";
    const ctaColor = isWorking ? hex : "text.secondary";
    const iconBg = isWorking ? alpha(hex, 0.1) : "rgba(0,0,0,0.06)";

    const label = isWorking ? "WORKING TIME" : "OFF HOURS";
    const durationLabel =
        hours > 0 ? `${hours}H ${mins > 0 ? `${mins}MIN` : ""}` : `${mins}MIN`;

    return (
        <>
            <Stack
                direction="row"
                width="100%"
                alignItems="stretch"
                spacing={1.5}
            >
                {/* Time column */}
                <Stack
                    direction="column"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{ flex: "0 0 auto", width: 44, pt: 1.5, pb: 1.5 }}
                >
                    <Typography
                        fontFamily={FONTS.display}
                        fontWeight={600}
                        fontSize="13px"
                        color="text.secondary"
                    >
                        {slot.start.format("HH:mm")}
                    </Typography>
                    <Typography
                        fontFamily={FONTS.display}
                        fontWeight={600}
                        fontSize="13px"
                        color="text.secondary"
                    >
                        {slot.end.format("HH:mm")}
                    </Typography>
                </Stack>

                {/* Card */}
                <ButtonBase
                    onClick={handleOpenChooser}
                    sx={{
                        flex: 1,
                        borderRadius: CARD_STYLES.borderRadius,
                        textAlign: "left",
                    }}
                >
                    <Stack
                        direction="column"
                        justifyContent="flex-start"
                        alignItems="flex-start"
                        width="100%"
                        sx={{
                            ...CARD_STYLES,
                            p: 1.5,
                            bgcolor: bgColor,
                            border: borderStyle,
                            boxShadow: "none",
                        }}
                        spacing={1}
                    >
                        {/* Section label */}
                        <Typography
                            fontSize="11px"
                            fontWeight={600}
                            color="text.secondary"
                            sx={{
                                textTransform: "uppercase",
                                letterSpacing: "0.5px",
                            }}
                        >
                            {label} · {durationLabel}
                        </Typography>

                        {/* CTA */}
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <Box
                                sx={{
                                    width: 28,
                                    height: 28,
                                    borderRadius: "8px",
                                    bgcolor: iconBg,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    flex: "0 0 auto",
                                }}
                            >
                                <AddRounded
                                    sx={{ fontSize: 18, color: ctaColor }}
                                />
                            </Box>
                            <Typography
                                fontFamily={FONTS.display}
                                fontWeight={600}
                                fontSize="14px"
                                color={ctaColor}
                            >
                                Add event
                            </Typography>
                        </Stack>
                    </Stack>
                </ButtonBase>
            </Stack>

            {/* Chooser modal */}
            <AddEventModal
                open={chooserOpen}
                onClose={handleCloseChooser}
                onService={handleServiceAppointment}
                onPrivate={handlePrivateMatter}
            />

            {/* Private matter modal */}
            <BlockTimeModal
                open={blockModalOpen}
                onClose={() => setBlockModalOpen(false)}
                onConfirm={handleConfirmBlock}
                start={slot.start}
                end={slot.end}
            />
        </>
    );
};

export default FreeSlotItem;
